
import { Notification } from '@/types/notifications';
import { UnifiedUser } from '@/hooks/useUnifiedUser';
import { supabase } from '@/integrations/supabase/client';

export const unifiedNotificationService = {
  // Get notifications for unified user with enhanced debugging - now using database
  async getNotifications(currentUser: UnifiedUser | null): Promise<Notification[]> {
    if (!currentUser) {
      console.log('📭 SERVICE: No current user for notifications');
      return [];
    }

    try {
      console.log('📬 SERVICE: Getting notifications for user:', currentUser.username, currentUser.id, 'Type:', currentUser.userType);
      
      // Get both regular notifications and game notifications
      const [regularNotifications, gameNotifications] = await Promise.all([
        // Regular chat notifications
        supabase
          .from('notifications')
          .select('*')
          .eq('to_user_id', currentUser.id)
          .order('created_at', { ascending: false }),
        
        // Game notifications  
        supabase
          .from('game_notifications')
          .select('*')
          .eq('recipient_id', currentUser.id)
          .eq('recipient_type', currentUser.userType)
          .order('sent_at', { ascending: false })
      ]);

      if (regularNotifications.error) {
        console.error('❌ SERVICE: Database error getting regular notifications:', regularNotifications.error);
      }
      
      if (gameNotifications.error) {
        console.error('❌ SERVICE: Database error getting game notifications:', gameNotifications.error);
      }
      
      const totalNotifications = (regularNotifications.data?.length || 0) + (gameNotifications.data?.length || 0);
      console.log('📬 SERVICE: Total notifications from database:', totalNotifications);
      console.log('📬 SERVICE: Regular notifications:', regularNotifications.data?.length || 0);
      console.log('📬 SERVICE: Game notifications:', gameNotifications.data?.length || 0);
      
      // Convert regular notifications
      const userChatNotifications = (regularNotifications.data || [])
        .filter(notif => {
          // Only include notifications that have the required chat notification fields and are mentions
          return notif.notification_type === 'mention' && notif.from_user_id && notif.message_preview;
        })
        .map(notif => ({
          id: notif.id,
          type: notif.notification_type,
          chat_group_id: notif.chat_group_id,
          message_id: notif.message_id,
          from_user_id: notif.from_user_id,
          to_user_id: notif.to_user_id,
          message_preview: notif.message_preview,
          chat_group_name: notif.chat_group_name,
          from_username: notif.from_username,
          created_at: notif.created_at,
          is_read: notif.is_read
        } as Notification));

      // Convert game notifications to our notification format
      const userGameNotifications = (gameNotifications.data || [])
        .map(notif => ({
          id: notif.id,
          type: notif.notification_type,
          message_preview: notif.message,
          from_username: 'System', // Game notifications are from system
          from_user_id: 'system',
          to_user_id: notif.recipient_id,
          created_at: notif.sent_at,
          is_read: notif.is_read,
          game_id: notif.game_id,
          requires_action: notif.requires_action,
          action_type: notif.action_type
        } as Notification & { game_id?: string, requires_action?: boolean, action_type?: string }));
      
      // Combine all notifications and sort by date
      const allNotifications = [...userChatNotifications, ...userGameNotifications]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      console.log(`📬 SERVICE: Found ${allNotifications.length} notifications for user:`, currentUser.username);
      
      return allNotifications;
    } catch (error) {
      console.error('❌ SERVICE: Error getting notifications:', error);
      return [];
    }
  },

  async getUnreadCount(currentUser: UnifiedUser | null): Promise<number> {
    const notifications = await this.getNotifications(currentUser);
    const unreadCount = notifications.filter(notif => !notif.is_read).length;
    console.log(`🔔 SERVICE: Unread notifications count: ${unreadCount} for user:`, currentUser?.username);
    return unreadCount;
  },

  async getMentions(currentUser: UnifiedUser | null): Promise<Notification[]> {
    const notifications = await this.getNotifications(currentUser);
    const mentions = notifications.filter(notif => notif.type === 'mention');
    console.log(`@️⃣ SERVICE: Found ${mentions.length} mentions for user:`, currentUser?.username);
    return mentions;
  },

  // ENHANCED: Create notification with comprehensive logging and database storage
  async createNotification(notification: Omit<Notification, 'id' | 'created_at'>): Promise<void> {
    try {
      console.log('=== SERVICE: CREATING NOTIFICATION ===');
      console.log('Type:', notification.type);
      console.log('To User:', notification.to_user_id);
      console.log('From User:', notification.from_username);
      console.log('Message Preview:', notification.message_preview.substring(0, 50));
      console.log('Chat Group:', notification.chat_group_name);
      
      // Validate required fields
      if (!notification.to_user_id || !notification.from_user_id || !notification.message_preview) {
        console.error('❌ SERVICE: Missing required notification fields:', {
          to_user_id: !!notification.to_user_id,
          from_user_id: !!notification.from_user_id,
          message_preview: !!notification.message_preview
        });
        return;
      }

      // Detect user type for the recipient
      const { data: internalUser } = await supabase
        .from('internal_users')
        .select('id')
        .eq('id', notification.to_user_id)
        .eq('is_active', true)
        .single();

      const { data: supabaseUser } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', notification.to_user_id)
        .single();

      const recipientUserType = internalUser ? 'internal' : (supabaseUser ? 'supabase' : 'internal');
      
      console.log('🔍 SERVICE: Recipient user type detected:', recipientUserType, 'for user:', notification.to_user_id);
      
      // Insert notification into database with correct user type
      const { data, error } = await supabase
        .from('notifications')
        .insert({
          // Use correct user_id and user_type combination for RLS
          user_id: notification.to_user_id,
          user_type: recipientUserType,
          notification_type: notification.type,
          category: 'chat',
          title: `Mention from ${notification.from_username}`,
          message: notification.message_preview,
          // Chat-specific fields
          to_user_id: notification.to_user_id,
          from_user_id: notification.from_user_id,
          message_preview: notification.message_preview,
          chat_group_id: notification.chat_group_id,
          message_id: notification.message_id,
          chat_group_name: notification.chat_group_name,
          from_username: notification.from_username,
          is_read: false
        })
        .select()
        .single();

      if (error) {
        console.error('❌ SERVICE: Database error creating notification:', error);
        console.error('Error details:', error.message);
        console.error('Failed insert data:', {
          user_id: notification.to_user_id,
          user_type: recipientUserType,
          notification_type: notification.type
        });
        return;
      }
      
      console.log('✅ SERVICE: Notification stored successfully in database:', data.id);
      
      // Create notification object for broadcasting
      const newNotification: Notification = {
        id: data.id,
        type: notification.type,
        chat_group_id: notification.chat_group_id,
        message_id: notification.message_id,
        from_user_id: notification.from_user_id,
        to_user_id: notification.to_user_id,
        message_preview: notification.message_preview,
        chat_group_name: notification.chat_group_name,
        from_username: notification.from_username,
        created_at: data.created_at,
        is_read: false
      };

      // COMPREHENSIVE broadcast with multiple approaches
      this.broadcastNotification(newNotification);
      
    } catch (error) {
      console.error('❌ SERVICE: Error creating notification:', error);
    }
  },

  // ENHANCED broadcast system with multiple reliability layers
  broadcastNotification(notification: Notification): void {
    try {
      console.log('=== SERVICE: BROADCASTING NOTIFICATION ===');
      console.log('Broadcasting for user:', notification.to_user_id);
      console.log('Notification type:', notification.type);
      console.log('Notification ID:', notification.id);
      
      // Create comprehensive event set for maximum compatibility
      const events = [
        // Primary unified events
        new CustomEvent('unified-notification', { 
          detail: notification,
          bubbles: true 
        }),
        new CustomEvent('unified-notification-update', { 
          detail: { type: 'new', notification },
          bubbles: true 
        }),
        
        // Legacy compatibility events
        new CustomEvent('new-notification', { 
          detail: notification,
          bubbles: true 
        }),
        new CustomEvent('notification-update', { 
          detail: { type: 'new', notification },
          bubbles: true 
        }),
        new CustomEvent('notification-bell-update', { 
          detail: { 
            notification: notification
          },
          bubbles: true 
        }),
        
        // Manager update event
        new CustomEvent('notification-manager-update', {
          detail: { notification, timestamp: Date.now() },
          bubbles: true
        }),

        // Enhanced notification trigger
        new CustomEvent('enhanced-notification-trigger', {
          detail: { notification, timestamp: Date.now() },
          bubbles: true
        })
      ];

      // Immediate dispatch to both window and document
      events.forEach(event => {
        window.dispatchEvent(event);
        document.dispatchEvent(event);
      });
      console.log('✅ SERVICE: Immediate events dispatched:', events.length);
      
      // Delayed dispatch for additional reliability (100ms)
      setTimeout(() => {
        events.forEach(event => {
          window.dispatchEvent(event);
          document.dispatchEvent(event);
        });
        console.log('✅ SERVICE: Delayed events dispatched (100ms)');
      }, 100);
      
      // Storage event simulation for cross-tab synchronization (200ms)
      setTimeout(() => {
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'user_notifications',
          newValue: localStorage.getItem('user_notifications'),
          oldValue: null,
          storageArea: localStorage
        }));
        console.log('✅ SERVICE: Storage event simulated for cross-tab sync');
      }, 200);
      
      // Final verification broadcast (500ms)
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('notification-verification', {
          detail: { 
            notificationId: notification.id,
            userId: notification.to_user_id,
            timestamp: Date.now()
          }
        }));
        console.log('✅ SERVICE: Final verification broadcast completed');
      }, 500);
      
    } catch (error) {
      console.error('❌ SERVICE: Error broadcasting notification:', error);
    }
  },

  async markAsRead(notificationId: string, isGameNotification: boolean = false): Promise<void> {
    try {
      console.log('📖 SERVICE: Marking notification as read:', notificationId, 'Game notification:', isGameNotification);
      
      if (isGameNotification) {
        // Mark game notification as read
        const { error } = await supabase
          .from('game_notifications')
          .update({ is_read: true })
          .eq('id', notificationId);

        if (error) {
          console.error('❌ SERVICE: Database error marking game notification as read:', error);
          return;
        }
      } else {
        // Mark regular notification as read
        const { error } = await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('id', notificationId);

        if (error) {
          console.error('❌ SERVICE: Database error marking notification as read:', error);
          return;
        }
      }
      
      this.broadcastUpdate();
      console.log('✅ SERVICE: Notification marked as read');
    } catch (error) {
      console.error('❌ SERVICE: Error marking notification as read:', error);
    }
  },

  async markAllAsRead(currentUser: UnifiedUser | null): Promise<void> {
    if (!currentUser) return;

    try {
      console.log('📖 SERVICE: Marking all notifications as read for user:', currentUser.username);
      
      // Mark both regular and game notifications as read
      const [regularUpdate, gameUpdate] = await Promise.all([
        supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('to_user_id', currentUser.id),
        
        supabase
          .from('game_notifications')
          .update({ is_read: true })
          .eq('recipient_id', currentUser.id)
          .eq('recipient_type', currentUser.userType)
      ]);

      if (regularUpdate.error) {
        console.error('❌ SERVICE: Database error marking regular notifications as read:', regularUpdate.error);
      }
      
      if (gameUpdate.error) {
        console.error('❌ SERVICE: Database error marking game notifications as read:', gameUpdate.error);
      }
      
      this.broadcastUpdate();
      console.log('✅ SERVICE: All notifications marked as read');
    } catch (error) {
      console.error('❌ SERVICE: Error marking all as read:', error);
    }
  },

  broadcastUpdate(): void {
    console.log('📡 SERVICE: Broadcasting notification update...');
    const events = [
      new CustomEvent('unified-notification-update', { detail: { type: 'update' } }),
      new CustomEvent('new-notification', { detail: null }),
      new CustomEvent('notification-update', { detail: { type: 'update' } }),
      new CustomEvent('notification-bell-update', { detail: { type: 'update' } }),
      new CustomEvent('notification-manager-update', { detail: { timestamp: Date.now() } }),
      new CustomEvent('enhanced-notification-trigger', { detail: { timestamp: Date.now() } })
    ];

    events.forEach(event => {
      window.dispatchEvent(event);
      document.dispatchEvent(event);
    });
    console.log('✅ SERVICE: Notification update broadcast complete');
  }
};
